/*
 * Einstein sticky add-to-cart bar.
 *
 * Shows a fixed buy bar on small screens as soon as the real add-to-cart
 * button has scrolled out of view, and hides it again when the button is
 * back on screen. No external libraries: one IntersectionObserver plus a
 * MutationObserver that mirrors the live price into the bar.
 *
 * Markup contract (see snippets/einstein-sticky-atc.liquid):
 *   <einstein-sticky-atc
 *     data-target="ProductSubmitButton-<section id>"
 *     data-price-source="price-<section id>">
 */
class EinsteinStickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-sticky-bar]');
    this.button = this.querySelector('[data-sticky-submit]');
    this.priceTarget = this.querySelector('[data-sticky-price]');

    this.targetId = this.dataset.target;
    this.priceSourceId = this.dataset.priceSource;

    if (!this.bar || !this.targetId) return;

    this.target = document.getElementById(this.targetId);
    if (!this.target) return;

    this.bindSubmit();
    this.observeTarget();
    this.syncPrice();
    this.observePrice();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  bindSubmit() {
    if (!this.button) return;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const liveTarget = document.getElementById(this.targetId);
      if (!liveTarget || liveTarget.hasAttribute('disabled')) return;
      // Clicking Dawn's own button keeps all of its cart logic intact.
      liveTarget.click();
    });
  }

  observeTarget() {
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle(scrolledPast);
        });
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );

    this.intersectionObserver.observe(this.target);
  }

  toggle(visible) {
    this.bar.classList.toggle('is-visible', visible);
    this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  syncPrice() {
    if (!this.priceTarget || !this.priceSourceId) return;

    const source = document.getElementById(this.priceSourceId);
    if (!source) return;

    const sale = source.querySelector('.price-item--sale, .price-item--regular');
    if (sale) this.priceTarget.textContent = sale.textContent.trim();

    const liveTarget = document.getElementById(this.targetId);
    if (liveTarget && this.button) {
      this.button.disabled = liveTarget.hasAttribute('disabled');
      const label = liveTarget.querySelector('span');
      const ownLabel = this.button.querySelector('span');
      if (label && ownLabel) ownLabel.textContent = label.textContent.trim();
    }
  }

  observePrice() {
    if (!this.priceSourceId || !('MutationObserver' in window)) return;

    const source = document.getElementById(this.priceSourceId);
    if (!source) return;

    this.mutationObserver = new MutationObserver(() => this.syncPrice());
    this.mutationObserver.observe(source, { childList: true, subtree: true, characterData: true });
  }
}

if (!customElements.get('einstein-sticky-atc')) {
  customElements.define('einstein-sticky-atc', EinsteinStickyAtc);
}
